/*
 * Copyright (c) 2022 automata-network/tgb-donation authors and contributors.
 * This software may be modified and distributed under the terms of MIT license.
 * Refer to the LICENSE file for details.
 */
import { HTMLProps, KeyboardEvent, useCallback, useEffect, useState } from 'react'

import BigNumberJS from 'bignumber.js'
import styled, { css } from 'styled-components'

import { DONATION_SGD_AMOUNT_MAX, DONATION_SGD_AMOUNT_MIN, DONATION_SGD_AMOUNT_MIN_ALT } from '../../constants'
import { pxToVw } from '../../functions/units'
import { Nullable } from '../../types/main'

export type ErrorKey =
  | 'AMOUNT_INVALID_FORMAT'
  | 'AMOUNT_IS_EMPTY'
  | 'AMOUNT_IS_NEGATIVE'
  | 'AMOUNT_IS_ZERO'
  | 'AMOUNT_MAX_EXCEEDED'
  | 'AMOUNT_MIN_EXCEEDED'

type Props = Partial<
  Pick<HTMLProps<HTMLInputElement>, 'disabled' | 'placeholder' | 'required' | 'type'>
> & {
  className?: string
  // useTaxDeduction: boolean
  useParticipateInNft: boolean
  validator?: RegExp
  value?: string
  onChange: (value: string) => void
  onError?: (error: Nullable<ErrorKey>) => void
}

const InputText = ({
  className,
  disabled = false,
  placeholder,
  required = false,
  type = 'text',
  validator,
  value = '',
  // useTaxDeduction,
  useParticipateInNft,
  onChange,
  onError
}: Props) => {
  const [isInit, setIsInit] = useState<boolean>(true)
  const [error, setError] = useState<Nullable<ErrorKey>>(null)

  useEffect(() => {
    onError && onError(error)
  }, [error, onError])

  const validateInput = useCallback(
    (value: string) => {
      if (isInit) return

      let error: Nullable<ErrorKey> = null

      if (!value.length) {
        error = 'AMOUNT_IS_EMPTY'
      } else if (value.charAt(0) === '-') {
        error = 'AMOUNT_IS_NEGATIVE'
      } else if (Number(value) === 0) {
        error = 'AMOUNT_IS_ZERO'
      } else if (validator && !validator.test(value)) {
        const valueDecimalPlaces = (value.split('.')[1] || '').length
        error = Math.floor(Number(value)) === 0 && valueDecimalPlaces && valueDecimalPlaces <= 2 ? 'AMOUNT_MIN_EXCEEDED' : 'AMOUNT_INVALID_FORMAT'
      } else {
        const minAmount = !useParticipateInNft ? DONATION_SGD_AMOUNT_MIN_ALT : DONATION_SGD_AMOUNT_MIN
        const valueBN = new BigNumberJS(value.replace(',', ''))
        if (valueBN.isGreaterThan(DONATION_SGD_AMOUNT_MAX)) {
          error = 'AMOUNT_MAX_EXCEEDED'
        } else if (valueBN.isLessThan(minAmount)) {
          error = 'AMOUNT_MIN_EXCEEDED'
        }
      }

      setError(error)
    },
    [isInit, useParticipateInNft, validator]
  )

  const handleChange = useCallback(
    (inputtedValue: string) => {
      if (isInit) {
        setIsInit(false)
      }

      if (inputtedValue.charAt(0) === '.') {
        return onChange && onChange(`0${inputtedValue}`)
      }
      onChange && onChange(inputtedValue)
    },
    [isInit, onChange]
  )

  useEffect(() => {
    validateInput(value)
  }, [validateInput, value])

  const handleKeyDown = useCallback((e: KeyboardEvent<HTMLInputElement>) => {
    const key = e.key.toLowerCase()

    const isValidKey = /[0-9,.]|backspace|delete|arrow.+/.test(key)
    const isCut = !!(key === 'x' && (e.ctrlKey || e.metaKey))
    const isCopy = !!(key === 'c' && (e.ctrlKey || e.metaKey))
    const isPaste = !!(key === 'v' && (e.ctrlKey || e.metaKey))
    const isSelectAll = !!(key === 'a' && (e.ctrlKey || e.metaKey))

    if (!isValidKey && !(isCut || isCopy || isPaste || isSelectAll)) {
      e.preventDefault()
    }
  }, [])

  return (
    <StyledInput
      $error={!!error && error !== 'AMOUNT_MAX_EXCEEDED'}
      className={className}
      disabled={disabled}
      placeholder={placeholder}
      required={required}
      type={type}
      value={value}
      onChange={(e) => handleChange(e.currentTarget.value)}
      onKeyDown={handleKeyDown}
    />
  )
}

export default InputText

const StyledInput = styled.input<{
  $error: boolean
}>`
  height: ${pxToVw(44)};
  // max-height: 44px;
  appearance: none;
  padding-inline: ${pxToVw(13)};

  ${({ theme, $error }) => css`
    background: ${theme.colors.bg};
    border: 1px solid ${!$error ? theme.colors.gray200 : theme.colors.red};
    border-radius: ${pxToVw(theme.units.borderRadius.small)};
    font-size: ${pxToVw(theme.units.fontSize.body3)};
    color: ${!$error ? undefined : theme.colors.red};
  `}

  ${({ theme }) => css`
    ${theme.mediaQuery.mobile`
      height: ${pxToVw(44, 720)};
      padding-inline: ${pxToVw(13, 720)};
      border-radius: ${pxToVw(theme.units.borderRadius.small, 720)};
      font-size: ${pxToVw(theme.units.fontSize.body3, 720)};
    `}
  `}
`
